import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChallengeAnswer } from "../../directus/collections";
import { ChallengeDetails } from "../../types/cityEscape";

import EditIcon from "@mui/icons-material/Edit";
import JoinFullIcon from "@mui/icons-material/JoinFull";
import JoinRightIcon from "@mui/icons-material/JoinRight";
import { LoadingGuard } from "../../components/LoadingGuard";
import { EnigmaAcceptedAnswersBox } from "../../components/EnigmaAcceptedAnswersBox.compoenent";

export type EnigmaDetailsProps = {
  title?: string;
  enigma: ChallengeDetails | null;
  isLoading?: boolean;
};

export function EnigmaDetails({
  enigma,
  isLoading,
  title = "Enigma",
}: EnigmaDetailsProps) {
  if (isLoading) {
    return <div>Loading...</div>;
  }

  if(!isLoading){
    console.log("EnigmaDetails", { enigma, isLoading });
  }

  return (
    <Card variant="elevation">
      <CardHeader
        title={title}
        action={
          <Tooltip title="Modifica Enigma">
            <IconButton
              href={`https://admin.garipalli.com/admin/content/steps_challenge/${enigma?.id}`}
              target="_blank"
              disabled={!enigma?.id}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        }
      />
      <CardContent>
        <LoadingGuard isLoading={isLoading ?? false}>
          <Stack spacing={2}>
            <EnigmaContentSection enigma={enigma} />
            <EnigmaDetailsSolutionSection enigma={enigma} />
            <EnigmaDetailsHintsSection enigma={enigma} />
            <EnigmaDetailsAnswersSection enigma={enigma} />
            <EnigmaDetailsManagedAnswersSection enigma={enigma} />
          </Stack>
        </LoadingGuard>
      </CardContent>
    </Card>
  );
}

function EnigmaContentSection({ enigma }: { enigma: ChallengeDetails | null }) {
  return (
    <Stack spacing={2}>
      <Typography variant="caption" fontWeight={"bold"} gutterBottom>
        Formulazione attuale
      </Typography>
      <div
        dangerouslySetInnerHTML={{
          __html: enigma?.textContent || "Nessun enigma selezionato",
        }}
      />
    </Stack>
  );
}

function EnigmaDetailsSolutionSection({
  enigma,
}: {
  enigma: ChallengeDetails | null;
}) {
  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="caption" fontWeight={"bold"} gutterBottom>
          Soluzione
        </Typography>
        <div
          dangerouslySetInnerHTML={{
            __html:
              enigma?.data.translations[0].solution ||
              "Nessun enigma selezionato",
          }}
        />
      </Stack>
    </Stack>
  );
}

function EnigmaDetailsHintsSection({
  enigma,
}: {
  enigma: ChallengeDetails | null;
}) {
  return (
    <Stack spacing={2}>
      <Typography variant="caption" fontWeight={"bold"} gutterBottom>
        Ispirazioni
      </Typography>
      <List dense>
        {enigma?.data.translations[0].clues?.map((hint, i, arr) => (
          <ListItem
            key={i}
            disableGutters
            disablePadding
            divider={i !== arr.length - 1}
          >
            <ListItemIcon>#{i + 1}</ListItemIcon>
            <ListItemText
              primary={
                <div
                  className="challenge-hint"
                  dangerouslySetInnerHTML={{
                    __html: hint.clue || hint.media || "",
                  }}
                ></div>
              }
            />
          </ListItem>
        )) ?? (
          <ListItem disableGutters disablePadding>
            Nessuna ispirazione disponibile
          </ListItem>
        )}
      </List>
    </Stack>
  );
}

const ANSWER_ACCEPTANCE_SVG_ICON = {
  is_included: JoinRightIcon,
  exact_match: JoinFullIcon,
};

const ANSWER_ACCEPTANCE_ICON = {
  is_included: (
    <Tooltip title="Il messaggio contiene la parola">
      <SvgIcon component={ANSWER_ACCEPTANCE_SVG_ICON.is_included}></SvgIcon>
    </Tooltip>
  ),
  exact_match: (
    <Tooltip title="La risposta equivale a una di quelle definite">
      <SvgIcon component={ANSWER_ACCEPTANCE_SVG_ICON.exact_match}></SvgIcon>
    </Tooltip>
  ),
};

function EnigmaDetailsAnswersSection({
  enigma,
}: {
  enigma: ChallengeDetails | null;
}) {
  return (
    <EnigmaAcceptedAnswersBox
      label="Risposte accettate"
      answers={enigma?.data.possible_correct_answers || []}
      matchType={enigma?.data.answers_evaluation_rule as keyof typeof ANSWER_ACCEPTANCE_ICON}
    />
  );
}

function EnigmaDetailsManagedAnswersSection({
  enigma,
}: {
  enigma: ChallengeDetails | null;
}) {
  return (
    <Stack spacing={2}>
      <Typography variant="caption" fontWeight={"bold"} gutterBottom>
        Risposte gestite
      </Typography>
      <List disablePadding>
        {enigma?.data?.managed_answers
          ?.reduce((acc, answer) => {
            console.debug("Processing managed answer", answer);
            const bucket = acc.find(
              (b) => b.id === answer.answers_evaluation_rule
            );
            if (bucket) {
              bucket.values.push(...answer.answers);
            } else {
              acc.push({
                id: answer.answers_evaluation_rule,
                values: [...answer.answers],
              });
            }
            return acc;
          }, [] as { id: string; values: ChallengeAnswer[] }[])
          .map((item) => {
            return (
              <ListItem disablePadding key={item.id}>
                <ListItemIcon>
                  {
                    ANSWER_ACCEPTANCE_ICON[
                      item.id as keyof typeof ANSWER_ACCEPTANCE_ICON
                    ]
                  }
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Stack
                      direction={"row"}
                      spacing={1}
                      flexWrap={"wrap"}
                      rowGap={1}
                    >
                      {item.values
                        .sort((a, b) => a.value.localeCompare(b.value))
                        .map((value, i) => (
                          <Chip
                            // avatar={
                            //   <Avatar>
                            //     {value.occurrences ?? 0}
                            //   </Avatar>
                            // }
                            size="small"
                            key={i}
                            label={value.value}
                          />
                        ))}
                    </Stack>
                  }
                />
              </ListItem>
            );
          }) ?? "Nessuna risposta accettata disponibile"}
      </List>
    </Stack>
  );
}
